import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowLeft, BookOpen, CheckCircle2, ChevronRight, Circle, Compass, GraduationCap, Library, LockKeyhole, Map, PlayCircle, RefreshCw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";

const STATUS = { active: "En curso", completed: "Dominado", rejected: "Devuelto", cancelled: "Cancelado" };

const AcademyProgress = () => {
  const navigate = useNavigate();
  const [enrollments, setEnrollments] = useState([]);
  const [selected, setSelected] = useState("");
  const [course, setCourse] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");

  const loadMine = useCallback(async () => {
    try {
      const { data } = await api.get("/api/academy/mine");
      const rows = Array.isArray(data) ? data : [];
      setEnrollments(rows);
      const first = rows.find((row) => ["active", "completed"].includes(row.status));
      setSelected((current) => current || first?.course_slug || "");
      setError("");
    } catch (e) { setError(formatApiError(e)); }
  }, []);

  useEffect(() => { loadMine(); }, [loadMine]);

  useEffect(() => {
    if (!selected) { setCourse(null); return; }
    let cancelled = false;
    api.get(`/api/academy/courses/${selected}/content`)
      .then(({ data }) => { if (!cancelled) setCourse(data); })
      .catch((e) => { if (!cancelled) setError(formatApiError(e)); });
    return () => { cancelled = true; };
  }, [selected]);

  const complete = async (lessonId) => {
    setBusy(lessonId);
    try {
      await api.post(`/api/academy/courses/${selected}/lessons/${lessonId}/complete`);
      const { data } = await api.get(`/api/academy/courses/${selected}/content`);
      setCourse(data);
      await loadMine();
    } catch (e) { setError(formatApiError(e)); }
    finally { setBusy(""); }
  };

  const active = useMemo(() => enrollments.filter((row) => ["active", "completed"].includes(row.status)), [enrollments]);
  const currentEnrollment = course?.enrollment;
  const flatLessons = useMemo(() => (course?.modules || []).flatMap((module) => (module.lessons || []).map((lesson) => ({ ...lesson, moduleTitle: module.title }))), [course]);
  const nextLesson = flatLessons.find((lesson) => !currentEnrollment?.completed_lessons?.includes(lesson.id));

  return (
    <div className="mx-auto max-w-[1500px] pb-12" data-testid="academy-progress-page">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <button onClick={() => navigate("/app/academia")} className="inline-flex items-center gap-2 rounded-xl border border-white/10 px-4 py-2.5 font-mont text-[9px] uppercase tracking-[.14em] text-[#bbaa8b]"><ArrowLeft size={13}/> Academia</button>
        <div className="flex gap-2">
          <button onClick={() => navigate("/app/materiales")} className="inline-flex items-center gap-2 rounded-xl border border-white/10 px-4 py-2.5 font-mont text-[9px] uppercase tracking-[.14em] text-[#bbaa8b]"><Library size={13}/> Biblioteca</button>
          <button onClick={loadMine} className="inline-flex items-center gap-2 rounded-xl border border-white/10 px-4 py-2.5 font-mont text-[9px] uppercase tracking-[.14em] text-[#bbaa8b]"><RefreshCw size={12}/> Actualizar</button>
        </div>
      </div>

      <section className="relative mb-6 overflow-hidden rounded-[30px] border border-[#d4ab5e]/20 bg-[#0d0b08] p-6 md:p-8">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_12%_10%,rgba(212,171,94,.13),transparent_34%)]" />
        <div className="relative">
          <div className="flex items-center gap-2 font-mont text-[8px] uppercase tracking-[.3em] text-[#a9956b]"><Compass size={12}/> Tu sendero</div>
          <h1 className="mt-2 font-cinzel text-3xl text-[#f2deb2] md:text-4xl">Mapa de aprendizaje</h1>
          <p className="mt-3 max-w-3xl font-cormorant text-xl text-[#b8ad99]">Cada lección completada ilumina el recorrido. Los talleres, evaluaciones y recursos viven dentro del mismo territorio académico.</p>
        </div>
      </section>

      {error && <div className="mb-5 rounded-2xl border border-red-400/20 bg-red-950/20 p-4 font-mont text-sm text-red-200">{error}</div>}

      {!active.length ? (
        <div className="rounded-[28px] border border-white/8 bg-white/[.025] p-12 text-center">
          <GraduationCap className="mx-auto mb-4 text-[#c3a466]" size={42}/>
          <p className="font-cormorant text-xl text-[#b7ac99]">Aún no has abierto un sendero académico.</p>
          <button onClick={() => navigate("/app/academia")} className="mt-5 rounded-xl border border-[#d4ab5e]/35 px-5 py-3 font-mont text-[10px] uppercase tracking-[.14em] text-[#e4c886]">Explorar Academia</button>
        </div>
      ) : (
        <div className="grid gap-6 xl:grid-cols-[310px_1fr]">
          <aside className="space-y-3 xl:sticky xl:top-4 xl:self-start">
            <div className="mb-3 font-mont text-[8px] uppercase tracking-[.28em] text-[#887d69]">Tus territorios</div>
            {active.map((row) => (
              <button key={row.id} onClick={() => setSelected(row.course_slug)} className={`w-full rounded-[22px] border p-4 text-left transition ${selected === row.course_slug ? "border-[#d4ab5e]/40 bg-[#d4ab5e]/7" : "border-white/8 bg-white/[.02]"}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="font-cinzel text-base text-[#ead4a7]">{row.course_title}</div>
                  <span className="font-mont text-[7px] uppercase tracking-[.14em] text-[#918570]">{STATUS[row.status] || row.status}</span>
                </div>
                <div className="mt-4 h-1.5 overflow-hidden rounded-full bg-white/7">
                  <div className="h-full rounded-full bg-[#d4ab5e]" style={{ width: `${row.progress_percent}%` }} />
                </div>
                <div className="mt-2 flex justify-between font-mont text-[8px] text-[#857b6b]"><span>{row.progress_percent}%</span><span>{row.completed_lessons.length}/{row.total_lessons}</span></div>
              </button>
            ))}
          </aside>

          {course && (
            <section className="overflow-hidden rounded-[30px] border border-white/9 bg-[#0f0d0a]">
              <div className="border-b border-white/8 p-5 md:p-7">
                <div className="flex flex-col justify-between gap-5 md:flex-row md:items-end">
                  <div>
                    <div className="font-mont text-[8px] uppercase tracking-[.28em] text-[#95866c]">Modo Estudio KD9</div>
                    <h2 className="mt-2 font-cinzel text-2xl text-[#f2ddb1] md:text-3xl">{course.title}</h2>
                    <p className="mt-2 max-w-3xl font-cormorant text-lg text-[#aaa08e]">{course.description}</p>
                  </div>
                  <div className="min-w-[170px] rounded-2xl border border-white/8 bg-white/[.02] p-4 text-center">
                    <div className="font-cinzel text-2xl text-[#e7c57f]">{currentEnrollment?.progress_percent || 0}%</div>
                    <div className="mt-1 font-mont text-[8px] uppercase tracking-[.16em] text-[#7f7565]">Dominio actual</div>
                  </div>
                </div>
              </div>

              <div className="p-5 md:p-7">
                {nextLesson && (
                  <div className="mb-7 rounded-[24px] border border-[#d4ab5e]/20 bg-[#d4ab5e]/5 p-5">
                    <div className="font-mont text-[8px] uppercase tracking-[.2em] text-[#aa946a]">Siguiente hito</div>
                    <div className="mt-2 flex flex-wrap items-center justify-between gap-4">
                      <div>
                        <div className="font-cinzel text-xl text-[#edd5a3]">{nextLesson.title}</div>
                        <div className="mt-1 font-cormorant text-base text-[#a69b88]">{nextLesson.moduleTitle}</div>
                      </div>
                      <button onClick={() => document.getElementById(`lesson-${nextLesson.id}`)?.scrollIntoView({ behavior: "smooth", block: "center" })} className="inline-flex items-center gap-2 rounded-xl border border-[#d4ab5e]/30 px-4 py-2.5 font-mont text-[9px] uppercase tracking-[.13em] text-[#e1c382]">Continuar <ChevronRight size={13}/></button>
                    </div>
                  </div>
                )}

                <div className="space-y-7">
                  {(course.modules || []).map((module, moduleIndex) => {
                    const moduleDone = (module.lessons || []).length > 0 && (module.lessons || []).every((lesson) => currentEnrollment.completed_lessons.includes(lesson.id));
                    return (
                      <section key={module.id} className="relative pl-8">
                        <div className="absolute bottom-[-30px] left-[12px] top-5 w-px bg-gradient-to-b from-[#d4ab5e]/45 to-white/5" />
                        <div className={`absolute left-0 top-0 flex h-6 w-6 items-center justify-center rounded-full border ${moduleDone ? "border-[#d4ab5e] bg-[#d4ab5e] text-[#171008]" : "border-[#d4ab5e]/35 bg-[#15110b] text-[#d4ab5e]"}`}>
                          {moduleDone ? <CheckCircle2 size={14}/> : <span className="font-mont text-[8px]">{moduleIndex + 1}</span>}
                        </div>
                        <div className="mb-3 flex items-center gap-3">
                          <h3 className="font-cinzel text-xl text-[#e8d2a5]">{module.title}</h3>
                          {moduleDone && <span className="rounded-full border border-[#d4ab5e]/20 px-2 py-1 font-mont text-[7px] uppercase tracking-[.14em] text-[#c9ae76]">Territorio abierto</span>}
                        </div>
                        <div className="space-y-3">
                          {(module.lessons || []).map((lesson) => {
                            const done = currentEnrollment.completed_lessons.includes(lesson.id);
                            return (
                              <article id={`lesson-${lesson.id}`} key={lesson.id} className={`rounded-[20px] border p-4 md:p-5 ${done ? "border-[#d4ab5e]/18 bg-[#d4ab5e]/[.035]" : "border-white/8 bg-white/[.018]"}`}>
                                <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                                  <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border ${done ? "border-[#d4ab5e]/35 text-[#d4ab5e]" : "border-white/10 text-[#8f8573]"}`}>
                                    {done ? <CheckCircle2 size={18}/> : lesson.content_url ? <PlayCircle size={18}/> : <Circle size={18}/>}
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <div className="font-cinzel text-base text-[#e9d6ae]">{lesson.title}</div>
                                    <div className="mt-1 font-cormorant text-base text-[#978d7d]">{lesson.summary || "Contenido del sendero"} {lesson.duration_min ? `· ${lesson.duration_min} min` : ""}</div>
                                    <div className="mt-3 flex flex-wrap gap-2">
                                      {lesson.content_url && <a href={lesson.content_url} target="_blank" rel="noreferrer" className="rounded-lg border border-white/10 px-3 py-2 font-mont text-[8px] uppercase tracking-[.12em] text-[#bca987]"><PlayCircle size={11} className="mr-1 inline"/> Abrir contenido</a>}
                                      {lesson.material_url && <a href={lesson.material_url} target="_blank" rel="noreferrer" className="rounded-lg border border-white/10 px-3 py-2 font-mont text-[8px] uppercase tracking-[.12em] text-[#bca987]"><BookOpen size={11} className="mr-1 inline"/> Material</a>}
                                    </div>
                                  </div>
                                  <button disabled={done || busy === lesson.id} onClick={() => complete(lesson.id)} className={`min-h-11 rounded-xl px-4 font-mont text-[9px] uppercase tracking-[.12em] disabled:opacity-50 ${done ? "border border-[#d4ab5e]/15 text-[#a99772]" : "border border-[#d4ab5e]/35 text-[#e1c382]"}`}>
                                    {done ? "Completada" : busy === lesson.id ? "Guardando…" : "Completar hito"}
                                  </button>
                                </div>
                              </article>
                            );
                          })}
                        </div>
                      </section>
                    );
                  })}
                </div>

                <div className="mt-9 grid gap-3 sm:grid-cols-3">
                  <button onClick={() => navigate("/app/materiales")} className="rounded-2xl border border-white/8 bg-white/[.02] p-4 text-left"><BookOpen size={18} className="text-[#c3a466]"/><div className="mt-2 font-cinzel text-sm text-[#e4cfa5]">Biblioteca</div><div className="mt-1 font-mont text-[8px] text-[#807665]">Materiales protegidos</div></button>
                  <button onClick={() => navigate("/app/examenes")} className="rounded-2xl border border-white/8 bg-white/[.02] p-4 text-left"><LockKeyhole size={18} className="text-[#c3a466]"/><div className="mt-2 font-cinzel text-sm text-[#e4cfa5]">Pruebas</div><div className="mt-1 font-mont text-[8px] text-[#807665]">Modo concentración</div></button>
                  <button onClick={() => navigate("/app/certificados")} className="rounded-2xl border border-white/8 bg-white/[.02] p-4 text-left"><Map size={18} className="text-[#c3a466]"/><div className="mt-2 font-cinzel text-sm text-[#e4cfa5]">Dominio</div><div className="mt-1 font-mont text-[8px] text-[#807665]">Logros y certificados</div></button>
                </div>
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
};

export default AcademyProgress;
